/**
 * Profile · Appearances (screenshots/44, brief §2.9). One card per meeting (frame, date, title, moment count,
 * Open meeting) with its moments: timestamp + AI summary + tag (Question / Opposed / Supported). Filters: All
 * meetings / City Council only, plus Show: All · Questions · Opposed · Supported. Side: Where they speak (moments
 * in the last 30 days by body) and How they engage (counts by kind of moment).
 */
import { useMemo, useState } from "react";
import { ArrowRight, Play } from "lucide-react";
import { clock } from "../lib/format";
import { List, ListRow, RowActions, RowCell, RowLead, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { PersonImg, meetingHref, mdy } from "./atoms";
import type { Appearance, PersonProfile } from "./model";

type Show = "all" | "question" | "oppose" | "support";
const TAG: Record<string, [string, string] | undefined> = { question: ["Question", "neutral"], oppose: ["Opposed", "negative"], support: ["Supported", "positive"] };
const SHOWN = 4;
const isCouncil = (a: Appearance) => /^City Council/i.test(a.body);

export function Appearances({ p }: { p: PersonProfile }) {
  const [council, setCouncil] = useState(false);
  const [show, setShow] = useState<Show>("all");
  const [all, setAll] = useState(false);
  const list = useMemo(() => p.appearancesList
    .filter((a) => !council || isCouncil(a))
    .map((a) => ({ ...a, moments: a.moments.filter((m) => show === "all" || m.stance === show) }))
    .filter((a) => a.moments.length), [p.appearancesList, council, show]);
  const shown = all ? list : list.slice(0, SHOWN);

  // Side cards
  const since = new Date(Date.now() - 30 * 864e5).toISOString().slice(0, 10);
  const where = new Map<string, number>();
  for (const a of p.appearancesList) if (a.date >= since) where.set(a.body, (where.get(a.body) ?? 0) + a.moments.length);
  const whereRows = [...where.entries()].sort((x, y) => y[1] - x[1]).slice(0, 4);
  const maxWhere = Math.max(1, ...whereRows.map(([, n]) => n));
  const moments = p.appearancesList.flatMap((a) => a.moments);
  const questions = moments.filter((m) => m.stance === "question").length, opposed = moments.filter((m) => m.stance === "oppose").length;

  return (
    <div className="p-cols">
      <div className="p-main p-main--20">
        <div className="p-subview">
          <div className="p-seg" role="radiogroup" aria-label="Meetings">
            <button role="radio" aria-checked={!council} onClick={() => setCouncil(false)}>All meetings · {p.appearancesList.length}</button>
            <button role="radio" aria-checked={council} onClick={() => setCouncil(true)}>City Council only</button>
          </div>
          <div className="p-show" role="radiogroup" aria-label="Show">
            <span>Show</span>
            {([["all", "All"], ["question", "Questions"], ["oppose", "Opposed"], ["support", "Supported"]] as const).map(([id, label]) => (
              <button key={id} role="radio" aria-checked={show === id} onClick={() => setShow(id)}>{label}</button>
            ))}
          </div>
        </div>
        {shown.map((a) => (
          <section key={a.id} className="p-mcard">
            <ListRow as="header" className="p-mcard__head">
              <RowLead><PersonImg image={a.frame} name={p.name} w={100} h={56} label /></RowLead>
              <RowMain min={140} className="p-mcard__title">
                <span className="p-eyebrow">{mdy(a.date, { month: "short", day: "numeric", year: "numeric" }).toUpperCase()}</span>
                <RowTitle as="b" lines={2}>{a.body}</RowTitle>
                <RowSub><small>{a.moments.length} {a.moments.length === 1 ? "moment" : "moments"}</small></RowSub>
              </RowMain>
              <RowActions><a className="btn" href={meetingHref(a.meetingId, a.moments[0]?.t)}><Play size={14} aria-hidden />Open meeting</a></RowActions>
            </ListRow>
            <List className="p-moments">
            {a.moments.map((m) => {
              const tag = TAG[m.stance];
              return (
                <ListRow key={m.id} className="p-moment">
                  <RowCell width={76}><a className="p-watch" href={meetingHref(a.meetingId, m.t)}><Play size={10} aria-hidden />{clock(m.t)}</a></RowCell>
                  <RowMain min={160}><RowSub wrap className="p-moment__text">{m.summary}</RowSub></RowMain>
                  {tag && <RowTrail><span className={`p-mtag p-mtag--${tag[1]}`}>{tag[0]}</span></RowTrail>}
                </ListRow>
              );
            })}
            </List>
          </section>
        ))}
        {!all && list.length > SHOWN && (
          <button className="link-arrow p-more-link" onClick={() => setAll(true)}>{list.length - SHOWN} earlier meetings<ArrowRight size={14} aria-hidden /></button>
        )}
        <p className="caveat">AI summaries of each moment. Watch the clip for the exact words.</p>
      </div>
      <aside className="p-side">
        {whereRows.length > 0 && (
          <section className="p-card">
            <h3>Where they speak</h3>
            <p className="p-card__sub">Moments on record in the last 30 days</p>
            <div className="p-bars">
              {whereRows.map(([body, n]) => (
                <div key={body} className="p-bars__row">
                  <span><span>{body}</span><b>{n}</b></span>
                  <span className="p-bars__track"><span style={{ width: `${(n / maxWhere) * 100}%` }} /></span>
                </div>
              ))}
            </div>
          </section>
        )}
        <section className="p-card">
          <h3>How they engage</h3>
          <div className="p-engage">
            <span><b>{questions}</b><small>questions</small></span>
            <span><b>{opposed}</b><small>opposed</small></span>
            <span><b>{moments.length - questions - opposed}</b><small>other</small></span>
          </div>
        </section>
      </aside>
    </div>
  );
}
